// import type { ImageItem } from "@/types/image";
// import type { WriteSimpliFiles } from "@/types/files";
import { useEffect } from "react";
import { availableLanguages } from "../i18nextinit";
import { getPdfSimpliBaseURL } from "../Services/base-url/api-constants";
import { ROUTE_CONSTANT } from "../routing/route-constants";
import { tempuserGuid } from "./constant";
import { getBlobUri, getInitializationVector, getServiceToken, getTenantId } from "./general-config";
// import DashboardOrchService from "@/Services/DashboardOrchService";

// Extend Window interface for GTM
declare global {
  interface Window {
    dataLayer: Record<string, any>[];
  }
}

export const readCookie = (name: any) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

export const createCookie = (name: string, value: string, days?: number, domain?: string) => {
  let expires = "";
  let _domain = "";
  if (days) {
    const date: any = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toGMTString();
  } else expires = "";

  if (domain) {
    _domain = "; domain=" + domain;
  }
  document.cookie = name + "=" + value + expires + _domain + "; path=/;";
};

export const deleteCookie = (name: string) => {
  document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
};

export const getAuthHeader = () => {
  let token = readCookie("jwtToken");
  const jwtToken = token ? token : getServiceToken();
  const headers = { Authorization: "Bearer" + " " + jwtToken };
  return headers;
};
export const normalizeGuid = (value: string | null): string => {
  if (!value) return "";

  const GUID_WITH_HYPHENS =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  const GUID_WITHOUT_HYPHENS = /^[0-9a-fA-F]{32}$/;

  if (GUID_WITH_HYPHENS.test(value)) {
    return value.toLowerCase();
  }

  if (GUID_WITHOUT_HYPHENS.test(value)) {
    return value.replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, "$1-$2-$3-$4-$5").toLowerCase();
  }

  return "";
};

export const createUUID = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const getLinkWithLocale = (locale: string, endPoint: string) => {
  return (!locale ? "" : "/" + locale) + "/" + endPoint;
};

const APP_ROUTE_SEGMENTS = new Set([
  "music-editor",
  "song-summary",
  "dashboard",
  "song-details",
  "track-song",
  "music-player",
  "account",
  "billing",
]);

/** Reads locale from `/app/{locale}/...` when present. */
export function getLocaleFromPathname(
  pathname: string = window.location.pathname,
): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "app" || segments.length < 2) {
    return "";
  }

  const candidate = segments[1].toLowerCase();
  if (APP_ROUTE_SEGMENTS.has(candidate)) {
    return "";
  }

  if (availableLanguages.includes(candidate) && candidate !== "en") {
    return candidate;
  }

  return "";
}

/** Prefers route param, then URL path, then i18next storage. */
export function resolveAppLocale(localeFromParams?: string): string {
  const fromParams = localeFromParams?.trim().toLowerCase();
  if (fromParams) {
    return fromParams;
  }

  const fromPath = getLocaleFromPathname();
  if (fromPath) {
    return fromPath;
  }

  const stored = (localStorage.getItem("i18nextLng") || "en").trim().toLowerCase();
  return stored === "en" ? "" : stored;
}

export function stripGuidHyphens(guid: string): string {
  return guid.replace(/-/g, "").toLowerCase();
}

export function isTempUserGuid(guid: string): boolean {
  if (!guid) return false;
  return (
    normalizeGuid(guid) === normalizeGuid(tempuserGuid) ||
    stripGuidHyphens(guid) === tempuserGuid
  );
}

/** Doc-profile API may store users with or without GUID hyphens — try both. */
export function getDocProfileUserIdVariants(guid: string): string[] {
  const dashed = normalizeGuid(guid);
  const undashed = stripGuidHyphens(dashed || guid);
  return [...new Set([dashed, undashed].filter(Boolean))];
}

export const getCleanUserGuid = () => {
  const userGuid = readCookie("userguid");
  const cleanGuid = userGuid?.replace(/-/g, "") || tempuserGuid;
  return cleanGuid;
};

export const getUserContainer = () => {
  const userGuid = getCleanUserGuid();
  return `${userGuid}`;
};

/** Azure blob folder for music API requests — matches WriteSimpli user_container pattern. */
export const getBlobContainer = () => {
  if (readCookie("jwtToken")) {
    return readCookie("userguid")?.replaceAll("-", "") ?? tempuserGuid;
  }
  return tempuserGuid;
};

export const isUserAuthenticated = () => {
  const userGuid = readCookie("userguid");
  const jwtToken = readCookie("jwtToken");
  return !!(userGuid && jwtToken);
};

export const isUserLoggedIn = () => {
  let isLoggedIn = false;
  if (!!readCookie("jwtToken") || !!readCookie("refreshToken")) {
    isLoggedIn = true;
  }
  return isLoggedIn;
};

/** Guest preview stops at 20s; refreshToken indicates a signed-in session. */
export const shouldLimitUnauthMusicPlayback = () => !readCookie("refreshToken");

function base64ToBytes(base64: any) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export async function decryptAES(encryptedBase64: string, keyString: string, ivString: string) {
  try {
    const keyBytes = new TextEncoder().encode(keyString);
    const iv = new TextEncoder().encode(ivString);

    const key = await crypto.subtle.importKey("raw", keyBytes, { name: "AES-CBC" }, false, [
      "decrypt",
    ]);

    const ciphertext = base64ToBytes(encryptedBase64);

    const decryptedBuffer = await crypto.subtle.decrypt({ name: "AES-CBC", iv }, key, ciphertext);

    const decoded = new TextDecoder().decode(decryptedBuffer);
    return decoded;
  } catch (err) {
    console.error("Decryption failed:", err);
    return null;
  }
}

export const PerquisiteBountyEnable = async (cipherText: string, userGuid: string) => {
  if (!cipherText) return false;

  const decrypted = await decryptAES(
    cipherText,
    userGuid.replaceAll("-", ""),
    getInitializationVector()
  );
  return decrypted?.trim().toLowerCase() === "true";
};

export const getQueryParam = (qparams: URLSearchParams, param: string) => {
  return qparams.get(param) ?? null;
};

export const CreateMultipleDocProfileRequest = (queryDataArray: any) => {
  const now = new Date();
  const folderPath =
    `${String(now.getMonth() + 1).padStart(2, "0")}_` +
    `${String(now.getDate()).padStart(2, "0")}_` +
    `${String(now.getFullYear()).slice(-2)}-` +
    `${String(now.getHours()).padStart(2, "0")}:` +
    `${String(now.getMinutes()).padStart(2, "0")}`;
  let docProfileData = {
    tenantId: getTenantId(),
    folderPath: folderPath,
    UploadDateTime: new Date().toISOString(),
    ModifiedDateTime: new Date().toISOString(),
    blobUrl: queryDataArray,
  };
  return docProfileData;
};

export const extractFileName = (fileUrl: string): string => {
  try {
    const url = new URL(fileUrl);
    const pathname = url.pathname;
    return decodeURIComponent(pathname.substring(pathname.lastIndexOf("/") + 1));
  } catch {
    return "";
  }
};

export const getFileNameWithoutGuid = (fileName?: string | null): string => {
  if (!fileName) return '';

  return fileName.replace(/_[a-f0-9]{32}(?=\.pdf$)/i, '');
};

// export const getNewFilesForDocProfile = (
//   filesToUse: WriteSimpliFiles[],
//   category:string,
//   usedFiles: { unqn: string }[]
// ) => {
//   const queryDataArray = filesToUse.map((file) => {
//     const file = file.files.unqn[0];
//     const relativePath = cleanUrl.split("pdfsimpliusers/")[1];
//     return { url: relativePath };
//   });

//   const usedUrls = new Set(usedFiles.map((x) => x.url));
//   const newFiles = queryDataArray.filter((x) => !usedUrls.has(x.url));

//   return { queryDataArray, newFiles };
// };

export const getBlobFileName = (userGuid?: string, fileName?: string) => {
  if (!userGuid || !fileName) return "";
  return `${getBlobUri()}pdfsimpliusers/${userGuid.replaceAll("-", "")}/${fileName}`;
};

export const getFileExtension = (filename:string) => {
  return filename.split('.').pop();
};

export const CreateDocProfileRequest = (userId:any, fileName:string, modifyFileName: string, category:string) => {
  let sizeinKB = 1560;
  let dId = createUUID();
  let modifiedUserId = userId?.replace(/-/g, "");
  let fileUrl = getBlobFileName(modifiedUserId, fileName);
  let modifyFileUrl = getBlobFileName(modifiedUserId, modifyFileName);

  const docProfileData = {
    Id: dId,
    OrignialDocumentId: dId,

    OriginalName: fileName,
    OriginalFileUrl: fileUrl,
    OriginalFileFormat: getFileExtension(fileName),

    UniqueName: fileName,
    UploadDateTime: new Date().toISOString(),
    ConvertedFileFormat: getFileExtension(modifyFileName),
    
    ModifiedName: modifyFileName,
    ModifieldFileUrl: modifyFileUrl,
    ModifiedDateTime: new Date().toISOString(),

    HasDocumentConverted: true,
    TenantId:getTenantId(),
    SizeInKB: sizeinKB,
    
    isForm: false,
    IsWelcomeDocument: false,
    IsSignSimipliDoc: false,
    IsAdvanceFormatDoc:false,
    CategoryName:category

  };
  return docProfileData;
};

// export const useGTM = (): void => {
//   useEffect(() => {
//     let gtmTagValue = "";
//     const currentUrl: string = window.location.href;

    
//     if (currentUrl.includes("pdfsimpli")) {
//       gtmTagValue = "GTM-53GR555";
//     } else if (currentUrl.includes("worksimpli")) {
//       gtmTagValue = "GTM-PBKBLH5M";
//     } else {
//       gtmTagValue = "GTM-53GR555"; // default
//     }
//     // Ensure dataLayer exists
//     window.dataLayer = window.dataLayer || [];

//     window.dataLayer.push({
//       event: "gtm.js",
//       "gtm.start": new Date().getTime(),
//     });

//     const script: HTMLScriptElement = document.createElement("script");
//     script.async = true;
//     script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmTagValue}`;
//     document.head.appendChild(script);

//     return () => {
//       document.head.removeChild(script);
//     };
//   }, []);
// };

export const useGTM = () => {
  useEffect(() => {
    var gtmTagValue = "";
    var currentUrl = window.location.href;
    if (currentUrl.indexOf("songsimpli") > -1) {
      gtmTagValue = "GTM-MTRVR9H5";
    } else if (currentUrl.indexOf("signsimpli") > -1) {
      gtmTagValue = "GTM-PC3HZGV";
    } else if (currentUrl.indexOf("pdfsimpli") > -1) {
      gtmTagValue = "GTM-53GR555";
    } else if (currentUrl.indexOf("resumebuild") > -1) {
      gtmTagValue = "GTM-59VL2P9";
    } else if (currentUrl.indexOf("legalsimpli") > -1) {
      gtmTagValue = "GTM-BP5G6XTR";
    } else if (currentUrl.indexOf("worksimpli") > -1) {
      gtmTagValue = "GTM-PBKBLH5M";
    } else {
      gtmTagValue = "GTM-53GR555"; // this is default value we have put will analyze later
    }
    // Define the dataLayer array if it does not exist
    window.dataLayer = window.dataLayer || [];

    // Push the initial GTM event
    window.dataLayer.push({
      event: "gtm.js",
      "gtm.start": new Date().getTime(),
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmTagValue}`;
    const firstHeadChild = document.head.firstChild;
    if (firstHeadChild) {
      document.head.insertBefore(script, firstHeadChild);
    } else {
      document.head.appendChild(script);
    }

    const noscript = document.createElement("noscript");
    const iframe = document.createElement("iframe");
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmTagValue}`;
    iframe.height = "0";
    iframe.width = "0";
    iframe.style.display = "none";
    iframe.style.visibility = "hidden";
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    return () => {
      script.remove();
      noscript.remove();
    };
  }, []);
};

export const getAssetPath = (path: string): string => {
  const base = import.meta.env.BASE_URL || "/app/writing-editor/";
  const cleanPath = path.replace(/^\.?\//, "");
  const baseWithSlash = base.endsWith("/") ? base : `${base}/`;
  return `${baseWithSlash}${cleanPath}`;
};

export const freshChatTitle = (): void => {
  const script: HTMLScriptElement = document.createElement("script");

  script.type = "text/javascript";
  script.src = "//in.fw-cdn.com/31678841/852408.js";
  script.setAttribute("chat", "true");

  document.head.appendChild(script);

  let chat_locale: string = window.location.href;

  if (chat_locale.includes("/es")) {
    chat_locale = "es";
  } else if (chat_locale.includes("/it")) {
    chat_locale = "it";
  } else if (chat_locale.includes("/fr")) {
    chat_locale = "fr";
  } else if (chat_locale.includes("/pt")) {
    chat_locale = "pt";
  } else {
    chat_locale = "en";
  }

  // Extend Window interface safely
  (window as Window & {
    fcWidgetMessengerConfig?: {
      locale: string;
    };
  }).fcWidgetMessengerConfig = {
    locale: chat_locale,
  };
};

export const isPaidUser = (isPremium: boolean,isGuestUser: boolean) => {
  return (isPremium || isGuestUser) ;
}; 

export const getLocalizedPath = (
  locale: string | undefined,
  route: { Path: string; LocalePath?: string }
) => {
  if (!locale || !route.LocalePath) return route.Path;
  return route.LocalePath.replace(":locale", locale);
};

/** PDFSimpli dashboard URL (MusicMaker SPA does not host /app/dashboard). */
export function getDashboardUrl(locale = ""): string {
  const localeVal = locale.trim().toLowerCase();
  const appBase = `${getPdfSimpliBaseURL}/app`;
  if (!localeVal || localeVal === "en") {
    return `${appBase}/dashboard/`;
  }
  return `${appBase}/${localeVal}/dashboard/`;
}

export function buildTrackSongUrl(
  docId: string,
  userId: string,
  locale = "",
  absolute = false,
): string {
  const path = getLocalizedPath(locale, {
    Path: ROUTE_CONSTANT.TRACK_SONG,
    LocalePath: ROUTE_CONSTANT.TRACK_SONG_LOCALE,
  });
  const params = new URLSearchParams({
    docId,
    uId: userId,
  });
  const relative = `${path}?${params.toString()}`;
  if (!absolute || typeof window === "undefined") {
    return relative;
  }
  return `${window.location.origin}${relative}`;
}

export function buildMusicPlayerUrl(
  docId: string,
  userId: string,
  locale = "",
  absolute = false,
): string {
  const path = getLocalizedPath(locale, {
    Path: ROUTE_CONSTANT.MUSIC_PLAYER,
    LocalePath: ROUTE_CONSTANT.MUSIC_PLAYER_LOCALE,
  });
  const params = new URLSearchParams({
    docId,
    uId: userId,
  });
  const relative = `${path}?${params.toString()}`;
  if (!absolute || typeof window === "undefined") {
    return relative;
  }
  return `${window.location.origin}${relative}`;
}